import { useCallback, useEffect, useRef, useState } from 'react';

import { useImageSearch } from '@/hooks/useImageSearch';
import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import { useTrackSearchByImageClickedAnalytics } from '@/utils/amplitude/analytics/searchByImage/trackSearchByImageClicked.analytics';

import { SearchByImageBottomSheet } from '../components/SearchByImageBottomSheet';
import { SearchByImagePopover } from '../components/SearchByImagePopover';

/**
 * Open/close wiring for the Search by Image trigger. Owns the popover (desktop) / bottom sheet
 * (mobile) surface, the desktop outside-click + Escape dismissal, and the open-time tracking +
 * stale-error clear.
 *
 * Attach `containerRef` to the wrapper that holds both the trigger and `surface`, so the
 * outside-click check treats clicks on the open popover as inside.
 */
export const useSearchByImageSurface = () => {
    const isMobile = useIsMobileLayout();
    const { clearError, status } = useImageSearch();
    const trackSearchByImageClicked = useTrackSearchByImageClickedAnalytics();
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // The mobile bottom sheet portals out of this container and manages its own
        // dismissal (overlay tap / drag / Escape), so the outside-click + Escape
        // handling below is only for the desktop anchored popover.
        if (!isOpen || isMobile) {
            return;
        }
        const handleDocumentClick = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
            }
        };
        document.addEventListener('click', handleDocumentClick);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('click', handleDocumentClick);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, isMobile]);

    const close = useCallback(() => {
        setIsOpen(false);
    }, []);

    const onTriggerClick = useCallback(() => {
        if (!isOpen) {
            // Clear a stale error so the surface reopens on a clean dropzone. This drops
            // only the error (not the results), so reopening over a results page keeps
            // those results and never redirects home.
            if (status === 'error') {
                clearError();
            }
            trackSearchByImageClicked();
        }
        setIsOpen((open) => !open);
    }, [clearError, isOpen, status, trackSearchByImageClicked]);

    const surface = isMobile ? (
        <SearchByImageBottomSheet
            onClose={close}
            open={isOpen}
        />
    ) : (
        isOpen && <SearchByImagePopover onClose={close} />
    );

    return {
        containerRef,
        isMobile,
        isOpen,
        onTriggerClick,
        surface,
    };
};
